import { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import "leaflet/dist/leaflet.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

import Home from "./pages/Home";
import Realities from "./pages/Realities";
import Reality from "./pages/Reality";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Events from "./pages/Events";
import Community from "./pages/Community";
import Advice from "./pages/Advice";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Profile from "./pages/Profile";
import Forum from "./pages/Forum";
import ForumPost from "./pages/ForumPost";
import Admin from "./pages/Admin";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export const AuthCtx = createContext({ user: null, loading: true, refresh: () => {} });
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch { setUser(null); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, []);

  return <AuthCtx.Provider value={{ user, loading, refresh, setUser }}>{children}</AuthCtx.Provider>;
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash || window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/"); return; }
    const sid = match[1];
    const inviteCode = sessionStorage.getItem("tc_invite") || null;
    (async () => {
      try {
        await axios.post(`${API}/auth/session`, { session_id: sid, invite_code: inviteCode });
        sessionStorage.removeItem("tc_invite");
        // Clear the hash from URL to avoid re-processing
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        navigate("/profilo", { replace: true });
      } catch (e) {
        console.error(e);
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-tc-bg" data-testid="auth-callback">
      <p className="text-tc-muted font-serif text-2xl italic">Ti stiamo accogliendo…</p>
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/realta" element={<Realities />} />
        <Route path="/realta/:id" element={<Reality />} />
        <Route path="/chi-siamo" element={<About />} />
        <Route path="/contatti" element={<Contact />} />
        <Route path="/eventi" element={<Events />} />
        <Route path="/comunita" element={<Community />} />
        <Route path="/consigli" element={<Advice />} />
        <Route path="/forum" element={<Forum />} />
        <Route path="/forum/:id" element={<ForumPost />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/profilo" element={<Profile />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App grain">
      <Toaster position="top-right" />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
